/**
 * Дашбоардын дээд тууз — цаа бугын гэрэл зураг дээрх мэндчилгээ.
 *
 * ⚠️ ЗУРГИЙН ЗАМ: `public/images/hero-reindeer.jpg`. Хэрэв тэр файл
 *    байхгүй бол тууз нь `bg-hero` нөөц өнгөөр хуучин шигээ дүүрэн
 *    харагдана — эвдрэхгүй, зүгээр л зурагтгүй болно. Гуравдагч
 *    домэйн ТЭГ (CLAUDE.md) тул CDN-ээс ХЭЗЭЭ Ч дуудахгүй, файл
 *    репод байрлана.
 *
 * ⚠️ CLS: зураг `absolute` тул layout-д ОГТ оролцохгүй — өндөр нь
 *    зөвхөн текстээс хамаарна. Тиймээс `next/image`-ийн aspect-ratio
 *    заль энд хэрэггүй, зураг хэдийд ирэхээс үл хамааран үсрэлт үүсэхгүй.
 *
 * ⚠️ SCRIM: зураг нь ЦАС — бараг цагаан. Хоёр өөр gradient хэрэглэнэ:
 *      мобайл  — доороос дээш (бүх текст доод талд байрлана)
 *      sm+     — зүүнээс баруун (текст зүүн, зураг баруун талд нээгдэнэ)
 *    Нэг хэвтээ gradient-ээр хоёуланг нь тайлбарлаж БОЛОХГҮЙ: 375px
 *    дээр «зүүн 22%» гэдэг чинь 82px — нэг ч үг багтахгүй.
 *
 *    Доорх хувиудыг ТААМАГЛААГҮЙ: `hero-reindeer.jpg`-ийн бодит
 *    пикселийг текст буудаг бүсэд уншиж, scrim-ийг нь дээр нь
 *    композит хийж хэмжсэн. 320–2560px өргөн, хоёр горимд хамгийн
 *    муу тохиолдол = 5.81:1 (`hero-ink-2`, AA нь 4.5).
 *    Зураг СОЛИВОЛ дахин хэмжинэ — цайвар зураг энэ нөөцийг иднэ.
 */

import type { CSSProperties } from 'react';
import { useAuth } from '../../lib/auth';
import { useRotatingGreeting } from '../../lib/use-rotating-greeting';
import { ROLE_LABEL } from '../../lib/types';
import { FxLiveChip } from '../fx-live';
import { HeroStream } from './hero-stream';

/** Репо доторх зам. Солих бол ЗӨВХӨН энэ мөр. */
const HERO_PHOTO = '/images/hero-reindeer.jpg';

const WEEKDAY = ['Ням', 'Даваа', 'Мягмар', 'Лхагва', 'Пүрэв', 'Баасан', 'Бямба'];
const MONTH_MN = [
  '1-р сарын', '2-р сарын', '3-р сарын', '4-р сарын', '5-р сарын', '6-р сарын',
  '7-р сарын', '8-р сарын', '9-р сарын', '10-р сарын', '11-р сарын', '12-р сарын',
];

/**
 * «Даваа · 2026 оны 8-р сарын 24» (CSS `uppercase` дарна).
 * Мэндчилгээтэй ижил цагийн бүсээр — эс тэгвээс Оросоос нэвтэрсэн
 * цуглуулагчид огноо, мэндчилгээ хоёр зөрөх боломжтой.
 */
function todayLabel(now: Date): string {
  // en-CA нь ISO дараалал өгнө: "2026-08-24"
  const [y, m, d] = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Ulaanbaatar',
    year: 'numeric', month: '2-digit', day: '2-digit',
  })
    .format(now)
    .split('-')
    .map(Number);

  // Гаригийг UTC дээр барьж тооцно — хөтчийн орон нутгийн цаг нөлөөлөхгүй
  const weekday = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d))).getUTCDay();

  return `${WEEKDAY[weekday]} · ${y} оны ${MONTH_MN[Number(m) - 1]} ${d}`;
}

export function HeroBand() {
  const { user, can } = useAuth();
  const { greetings, index } = useRotatingGreeting(user?.fullName);

  /*
    DARK GLASS (2026-10-05): тууз зүүнээс нээгдэж (`hero-reveal`), огноо →
    мэндчилгээ → дүр дараалан гарна. Дизайны «aurora» туузын оронд бодит
    цаа бугын зураг (handoff-ийн заавар), дээр нь цас (`HeroStream`).
  */
  const step = (i: number) => ({ '--i': i } as CSSProperties);

  return (
    <section className="hero-reveal relative overflow-hidden rounded-hero border border-white/7 bg-hero">
      {/*
        Дэвсгэр зураг. Утга агуулаагүй чимэглэл тул `alt=""` + `aria-hidden`.
        `fetchPriority="high"` нь Next.js-ийн `priority`-ийн Vite дэх дүйцэл —
        хөтөч үүнийг LCP нэр дэвшигч гэж үзэж эрт татна.
      */}
      <img
        src={HERO_PHOTO}
        alt=""
        aria-hidden
        fetchPriority="high"
        decoding="async"
        className="pointer-events-none absolute inset-0 size-full object-cover
          object-[50%_48%] sm:object-[50%_54%]"
      />

      {/*
        Мобайл — доороос дээш. Текст нь туузны доод ~12–67%-ийг эзэлдэг
        тул scrim тэр бүсэд α ≥ 0.80; дээд ~32% нь нээгдэж зураг
        харагдана. Тиймээс мобайлд тууз арай өндөр (168px) — эс тэгвээс
        зурагт зай үлдэхгүй, бүхэлдээ бараан хавтгай болно.
      */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-linear-to-t from-hero-scrim from-0%
          via-hero-scrim/78 via-68% to-hero-scrim/20 sm:hidden"
      />

      {/* sm+ — зүүнээс баруун. Баруун тал нээгдэж гүн мэдрэмж өгнө */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 hidden bg-linear-to-r from-hero-scrim
          from-22% via-hero-scrim/72 via-50% to-transparent sm:block"
      />

      {/*
        Урсгал — scrim-ийн ДЭЭР, контентын ДООР. Доогуур тавибал
        харанхуйлуулагч цэгүүдийг залгичихна; текстийн дээр гарвал
        уншихад саад болно.
      */}
      <HeroStream />

      <div className="relative flex min-h-[168px] flex-wrap items-end justify-between gap-4 px-4 py-5 sm:min-h-[220px] sm:items-center sm:px-10 sm:py-8">
        {/*
          ⚠️ `sm:max-w-[50%]` — gradient нь 50%-аас цааш тунгалагжиж
          эхэлдэг. Хязгаар тавихгүй бол урт кирилл нэр цайвар цасан
          хэсэг рүү орж уншигдахаа болино. Тайрахгүй, боож өгнө.
        */}
        <div className="flex min-w-0 flex-col gap-1.5 sm:max-w-[60%] sm:gap-3.5">
          <span
            style={step(4)}
            className="rise flex items-center gap-2.5 text-meta font-semibold tracking-[0.15em] text-hero-accent uppercase"
          >
            <span aria-hidden className="h-0.5 w-6 shrink-0 bg-hero-accent" />
            {todayLabel(new Date())}
          </span>

          {/*
            Мэндчилгээ бүгд НЭГ grid нүдэнд давхарлана — мөрийн өндөр нь
            хамгийн уртаараа тогтох тул солигдоход тууз үсрэхгүй.
            `translate`, `opacity` хоёр layout-д нөлөөлдөггүй.

            `aria-live="off"` — дэлгэц уншигч 5 секунд тутам дахин
            уншихгүй. Идэвхгүй хувилбарууд `aria-hidden` тул h1-ийн
            хүртээмжийн нэр нь ҮРГЭЛЖ ганц, харагдаж буй мэндчилгээ.
          */}
          <h1 aria-live="off" style={step(5)} className="rise grid text-h1 text-white sm:text-greeting">
            {greetings.map((g, i) => (
              <span
                key={g}
                aria-hidden={i !== index}
                className={`col-start-1 row-start-1 text-balance transition-[opacity,transform]
                  duration-400 ease-out ${
                    i === index
                      ? 'translate-y-0 opacity-100'
                      : 'pointer-events-none translate-y-2 opacity-0'
                  }`}
              >
                {g}
              </span>
            ))}
          </h1>

          {user ? (
            <span
              style={step(6)}
              className="rise inline-flex w-fit rounded-[14px] border border-white/10 bg-white/8 px-3.5 py-1.5
                text-meta font-semibold text-white"
            >
              {ROLE_LABEL[user.role]}
              {user.storeName ? ` · ${user.storeName}` : ''}
            </span>
          ) : null}
        </div>

        {/* Ханшийн мэдээлэл — зөвхөн захиралд.
            Жинхэнэ хамгаалалт нь backend дээр (`directorOnly`). */}
        {can('DIRECTOR_READONLY') ? <FxLiveChip /> : null}
      </div>
    </section>
  );
}
